var Geometry = require("./basicGeometry.js");
var Raphael = require("./minimalRaphaelPort.js");

/**
 * Calculates a list of points that approximate a bezier curve.
 * Precision defines the maximum distance between the real curve and the approximated polygon.
 * This function is designed to achieve approximately uniform accuracy by using more points where the gradient is larger.
 * @param  {Geometry.Coord} prev      previous point
 * @param  {array} bezier
 * @param  {float} precision
 * @return {array of Geometry.Coord}
 * One special case not completely covered: a bezier where p1, p2, p3, p4 are collinear with p2 and p3 not on segment [p1, p4]. i.e. so that the bezier forms a segment with extremities outside start and endpoint.
 */
Geometry.bezierToPolygonApproximation = function(prev, bezier, precision) {
    if(precision == undefined) console.warn("No precision argument");
    precision = precision || 0.005;
    console.assert(precision > 0, "Invalid precision");
    console.assert(bezier[0] == 'C');

    var p1 = prev;
    var p2 = new Geometry.Coord(bezier[1], bezier[2]);
    var p3 = new Geometry.Coord(bezier[3], bezier[4]);
    var p4 = new Geometry.Coord(bezier[5], bezier[6]);

    var points = [];
    //recursively inserts extra points until accuracy achieved
    var insertPoint = function(prevt, nextt) {
        if(nextt - prevt < 0.0001) return;  //failsafe
        var newt = (prevt + nextt) / 2;   //insert point approximately in the middle
        var prevp = Geometry.getBezierPoint(p1, p2, p3, p4, prevt);
        var nextp = Geometry.getBezierPoint(p1, p2, p3, p4, nextt);
        var newp = Geometry.getBezierPoint(p1, p2, p3, p4, newt);

        var accuracyAchieved;
        if(prevp.distance(nextp) > 0)
            accuracyAchieved = Geometry.Line.lineThrough(prevp, nextp).distance(newp) < precision;
        else
            accuracyAchieved = prevp.distance(newp) < precision;
        // var accuracyAchieved = new Geometry.Segment(prevp, nextp).distance(newp) < precision;
        var newpNotBetweenOld = Math.max(newp.distance(nextp), newp.distance(prevp)) > nextp.distance(prevp);
        if(!accuracyAchieved || newpNotBetweenOld) {
            insertPoint(prevt, newt);
            points.push(newp);
            insertPoint(newt, nextt);
        }
    };
    insertPoint(0, 0.5);
    points.push(Geometry.getBezierPoint(p1, p2, p3, p4, 0.5));
    insertPoint(0.5, 1);
    points.push(p4);
    return points;
};

//https://en.wikipedia.org/wiki/De_Casteljau's_algorithm
Geometry.getBezierPoint = function(p1, p2, p3, p4, t) {
    console.assert(t>=0 && t<=1);
    var mt = 1-t;
    var p12   = p1.mult(mt).add(p2.mult(t));
    var p23   = p2.mult(mt).add(p3.mult(t));
    var p34   = p3.mult(mt).add(p4.mult(t));
    var p123  = p12.mult(mt).add(p23.mult(t));
    var p234  = p23.mult(mt).add(p34.mult(t));
    var p1234 = p123.mult(mt).add(p234.mult(t));
    return p1234;
    // var Bx = Math.pow(1-t,3)*p1.x+3*t*Math.pow(1-t,2)*p2.x+3*(1-t)*Math.pow(t,2)*p3.x+Math.pow(t,3)*p4.x;
    // var By = Math.pow(1-t,3)*p1.y+3*t*Math.pow(1-t,2)*p2.y+3*(1-t)*Math.pow(t,2)*p3.y+Math.pow(t,3)*p4.y;
    // return new Geometry.Coord(Bx, By);
};

function solveMinMax(p1_, p2_, p3_, p4_) {
    var a = 3*(-p1_+3*p2_-3*p3_+p4_);
    var b = 6*(p1_-2*p2_+p3_);
    var c = 3*(p2_-p1_);
    var points_t = [0, 1];
    if(b*b-4*a*c > 0) {
        var sol1_t = (-b+Math.sqrt(b*b-4*a*c)) / (2*a);
        var sol2_t = (-b-Math.sqrt(b*b-4*a*c)) / (2*a);
        if(sol1_t > 0 && sol1_t < 1) {
            points_t.push(sol1_t);
        }
        if(sol2_t > 0 && sol2_t < 1) {
            points_t.push(sol2_t);
        }
    }

    return points_t;
}
//http://pomax.github.io/bezierinfo/#extremities
Geometry.bezierToBoundingBox = function(prev, bezier) {
    console.assert(bezier[0] == 'C');
    var p1 = prev;
    var p2 = new Geometry.Coord(bezier[1], bezier[2]);
    var p3 = new Geometry.Coord(bezier[3], bezier[4]);
    var p4 = new Geometry.Coord(bezier[5], bezier[6]);
    var x_sols = solveMinMax(p1.x, p2.x, p3.x, p4.x)
    .map(function(t) {
        return Geometry.getBezierPoint(p1, p2, p3, p4, t).x;
    });
    var y_sols = solveMinMax(p1.y, p2.y, p3.y, p4.y)
    .map(function(t) {
        return Geometry.getBezierPoint(p1, p2, p3, p4, t).y;
    });
    var xmin = Math.min.apply(null, x_sols);
    var xmax = Math.max.apply(null, x_sols);
    var ymin = Math.min.apply(null, y_sols);
    var ymax = Math.max.apply(null, y_sols);
    return new Geometry.BoundingBox.fromCoordinates(xmin, ymin, xmax, ymax);
};


Geometry.arcToBezierApproximation = function(prev, arc) {
    console.assert(arc[0] == 'A');

    // TODO write test for this.
    var flip = false;
    if(arc[0] < 0){
        arc[0] *= -1;
        // positive arcs only for Raphae
        flip = true;
    }
    if(arc[1] < 0){
        arc[1] *= -1;
       flip = true;
    }
    if(flip){
        arc[5] = (arc[5] + 1) % 2;
        // if arc values flip must flip this too
    }

    var c = Raphael.a2c(prev.x, prev.y, arc[1], arc[2], arc[3], arc[4], arc[5], arc[6], arc[7], false);
    var beziers = [];
    while(c.length) {
        beziers.push(['C', c[0], c[1], c[2], c[3], c[4], c[5]]);
        c.splice(0, 6);
    }
    return beziers;
};

Geometry.arcToPolygonApproximation = function(prev, arc, precision) {
    var beziers = Geometry.arcToBezierApproximation(prev, arc);
    var points = [];
    for(var i=0; i<beziers.length; i++) {
        var bezier = beziers[i];
        points = points.concat(Geometry.bezierToPolygonApproximation(prev, bezier, precision));
        prev = new Geometry.Coord(bezier[5], bezier[6]);
    }
    return points;
};

Geometry.arcToBoundingBoxApproximation = function(prev, arc) {
    var beziers = Geometry.arcToBezierApproximation(prev, arc);
    var bb = Geometry.BoundingBox.emptyBoundingBox;
    for(var i=0; i<beziers.length; i++) {
        var bezier = beziers[i];
        bb = bb.merge(Geometry.bezierToBoundingBox(prev, bezier));
        prev = new Geometry.Coord(bezier[5], bezier[6]);
    }
    return bb;
};


/**
 * Approximates a cubic Bezier curve using a set of circular arcs and line segments.
 * De Casteljau's algorithm with a twist!
 * See e.g. http://antigrain.com/research/adaptive_bezier/
 * iteration should be 0 on first call
 * precision: in sq units
 */
Geometry.bezierToArcsApproximation = function(p1,p2,p3,p4,iteration,precision){
    var p12   = p1.add(p2).mult(0.5); //new p2
    var p23   = p2.add(p3).mult(0.5);
    var p34   = p3.add(p4).mult(0.5);
    var p123  = p12.add(p23).mult(0.5); //new p3
    var p234  = p23.add(p34).mult(0.5);
    var p1234 = p123.add(p234).mult(0.5); //new p4
    if(!precision){
        precision = 100;
    }

    if(!iteration){
        iteration = 0;
    }

    var results = [];

    var stoppingCriterion = false;
    var makeArc = true;
    if(Geometry.collinear(p1,p1234,p4)){
        stoppingCriterion = true;
        makeArc = false;
    } else if(Geometry.bezierToLineSegmentDistance(p1,p2,p3,p4)<precision){
        stoppingCriterion = true;
    } else {
        var arc = Geometry.arcThrough3Points(p1,p1234,p4);
        if(Geometry.bezierToArcDistance(p1,p2,p3,p4,arc)<precision){
            stoppingCriterion = true;
        }
    }


    if(stoppingCriterion){
        //approximate Bezier curve with a single arc or a straight line

        if(!makeArc){
            //this is a straight line
            results.push({start:p1,end:p4});
        } else {
            //circle arc through 3 points: p1 , p1234, p4
            var arc = Geometry.arcThrough3Points(p1,p1234,p4);
            results.push(arc);
        }
    } else {
        //recursion
        Geometry.bezierToArcsApproximation(p1,p12,p123,p1234,iteration+1).forEach(function(arc){
           results.push(arc);
        });
        Geometry.bezierToArcsApproximation(p1234,p234,p34,p4,iteration+1).forEach(function(arc){
            results.push(arc);
        });
    }
    return results;
};


/**
 * Basic numerical integration using the trapezoidal rule
 * @param func function to integrate (1 dimensional)
 * @param a integrate from
 * @param b to
 * @param num_steps number of integration steps (default: 100)
 * @returns {number}
 */
Geometry.numericalIntegration = function(func,a,b,num_steps) {
    if (!num_steps) {
        num_steps = 100;
    }

    var sum = 0;

    sum += func(a);
    for(var i=1;i<num_steps;++i){
        var t = (b-a)/num_steps*i+a;
        sum += 2*func(t);
    }
    sum += func(b);

    var res = (b-a)/(2*num_steps)*sum;
    return res;
};

/**
 * Computes the area (positive) between a line segment defined by p1,p4 and an bezier curve defined by p1,p2,p3,p4
 * @param p1
 * @param p2
 * @param p3
 * @param p4
 * @returns {number}
 */
Geometry.bezierToLineSegmentDistance = function(p1,p2,p3,p4) {
    var x1 = p1.x;
    var x2 = p2.x;
    var x3 = p3.x;
    var x4 = p4.x;
    var y1 = p1.y;
    var y2 = p2.y;
    var y3 = p3.y;
    var y4 = p4.y;

    var func = function(t){
        //euclidean distance between Bezier curve and straight line
        var BLx = -3*t*t*x3*(t - 1) + 3*t*x2*(t - 1)*(t-1) + t*x4*(t*t - 1) - x1*(-t + (t - 1)*(t-1)*(t-1) + 1);
        var BLy = -3*t*t*y3*(t - 1) + 3*t*y2*(t - 1)*(t-1) + t*y4*(t*t - 1) - y1*(-t + (t - 1)*(t-1)*(t-1) + 1);
        var d = Math.sqrt(BLx*BLx+BLy*BLy);
        return d;
    };

    var res = Geometry.numericalIntegration(func,0,1);
    return res;
};

/**
 * Computes the area (positive) between a cubic Bezier curve defined by p1,p2,p3 and p4 and an arc
 * @param p1
 * @param p2
 * @param p3
 * @param p4
 * @param arc
 * @returns {number}
 */
Geometry.bezierToArcDistance = function(p1,p2,p3,p4,arc) {
    var x1 = p1.x;
    var x2 = p2.x;
    var x3 = p3.x;
    var x4 = p4.x;
    var y1 = p1.y;
    var y2 = p2.y;
    var y3 = p3.y;
    var y4 = p4.y;

    var func = function(t){
        var Bx = Math.pow(1-t,3)*x1+3*t*Math.pow(1-t,2)*x2+3*(1-t)*Math.pow(t,2)*x3+Math.pow(t,3)*x4;
        var By = Math.pow(1-t,3)*y1+3*t*Math.pow(1-t,2)*y2+3*(1-t)*Math.pow(t,2)*y3+Math.pow(t,3)*y4;

        var arcLength = arc.angleC-arc.angleA;

        var tC = (arc.angleA+t*(arc.angleC-arc.angleA))/180*Math.PI;

        var Cx = arc.center.x+arc.radius*Math.cos(tC);
        var Cy = arc.center.y+arc.radius*Math.sin(tC);

        var d = Math.sqrt(Math.pow(Bx-Cx,2)+Math.pow(By-Cy,2));
        return d;
    };

    var res = Geometry.numericalIntegration(func,0,1);
    return res;
};

/**
 * Finds an arc (circle segment) given 3 points A,B,C.
 * The arc connects points A and C and goes through B.
 *
 * @param A
 * @param B
 * @param C
 * @returns {{start: *, end: *, center: Geometry.Coord, radius: number, curve: number}} start: A, end: C, center: center of the circle through A,B,C, radius: radius of the circle through A,B,C, curve: size of the arc as a number of degrees
 */
Geometry.arcThrough3Points = function(A,B,C) {
    //see https://en.wikipedia.org/wiki/Circumscribed_circle#Triangles
    //find the radius of the circle that circumscribes the triangle p1,p2,p3
    var c = A.sub(B).length();
    var a = B.sub(C).length();
    var b = A.sub(C).length();

    //angles
    var sA = Math.acos((b*b+c*c-a*a)/(2*b*c));
    var sB = Math.acos((a*a+c*c-b*b)/(2*a*c));
    var sC = Math.acos((a*a+b*b-c*c)/(2*a*b));

    var r = a*b*c/
        Math.sqrt((a+b+c)*(b+c-a)*(c+a-b)*(a+b-c));

    var D = 2*(A.x*(B.y-C.y)+B.x*(C.y-A.y)+C.x*(A.y-B.y));
    var Ux = (
            (A.x*A.x+A.y*A.y)*(B.y-C.y)
            +(B.x*B.x+B.y*B.y)*(C.y-A.y)
            +(C.x*C.x+C.y*C.y)*(A.y-B.y)
        )/D;
    var Uy = (
            (A.x*A.x+A.y*A.y)*(C.x-B.x)
            +(B.x*B.x+B.y*B.y)*(A.x-C.x)
            +(C.x*C.x+C.y*C.y)*(B.x-A.x)
        )/D;
    //center of the circle
    var U = new Geometry.Coord(Ux,Uy);

    //let's make our life easier by translating to the origin
    var A0 = A.sub(U);
    var C0 = C.sub(U);

    //find the angle between the x-axis and C0 and the x-axis and A0
    var sC0 = Math.atan2(C0.y,C0.x);
    var sA0 = Math.atan2(A0.y,A0.x);
    //atan2 returns a negative angle for for y<0
    if(sA0<0){
        sA0 += Math.PI*2;
    }
    if(sC0<0){
        sC0 += Math.PI*2;
    }

    var curve = sA0-sC0; //this is the number of radians between A and C.
    //modulo 2 PI
    if(curve>Math.PI){
        curve = Math.PI*2-curve;
    } else if (curve<-Math.PI){
        curve = Math.PI*2+curve;
    }

    //This is a trick to know if the curve is CW or CCW
    if(Geometry.pointLineSide(A,B,C)<0){
        curve = -Math.abs(curve);
    } else {
        curve = Math.abs(curve);
    }

    //convert to degrees
    var curveDegrees = curve*180/Math.PI;
    return {start:A,end:C,center:U,radius:r,curve:curveDegrees,angleA:sA0*180/Math.PI,angleC:sC0*180/Math.PI};
};

/**
 * Approximates an Ellipse using a set of circular arc segments (currently fixed at 4 arc segments).
 * @param pos the center of the ellipse
 * @param rot the rotation of the ellipse
 * @param width width of the ellipse (x-axis/major axis diameter)
 * @param height height of the ellipse (y-axis/minor axis diameter)
 * @returns {Array} an array of maps containing start and end points of each arc and the number of degrees spanned by each arc.
 */
Geometry.ellipseToArcsApproximation = function(pos,rot,width,height){
    var a = width/2;
    var b = height/2;
    var k0 = a/(b*b);
    var kn = b/(a*b);
    var k1 = (k0+kn)/2;
    //x1,y1 are points with curvature equal to the average curvature of the top and sides of the ellipse
    //see http://www.geometrictools.com/Documentation/ApproximateEllipse.pdf
    var lambda1 = Math.pow(a*b/k1,2/3);
    var x1 = a*Math.sqrt(Math.abs((lambda1-a*a)/(b*b-a*a)));
    var y1 = b*Math.sqrt(Math.abs((lambda1-b*b)/(a*a-b*b)));

    var points = [
        [-x1,y1],
        [-x1,-y1],
        [x1,-y1],
        [x1,y1]
    ];

    var curve = 2*(Math.atan2(y1,x1)*180/Math.PI); //in degrees
    var pointsCoordTransformed = points.map(function(p){
        return new Geometry.Coord(p[0],p[1]).add(pos).rotate(rot,pos);
    });

    var result = [];

    for(var idx=0;idx<points.length;++idx){
        var currentCurve = curve;
        if(idx%2==0){
            currentCurve = 180-currentCurve;
        }
        result.push({start:pointsCoordTransformed[idx],end:pointsCoordTransformed[(idx+1)%(points.length)],curve:currentCurve});
    }
    return result;
};
